import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface SettingsState {
  workDuration: number;
  shortBreakDuration: number;
  longBreakDuration: number;
  longBreakInterval: number;
  soundEnabled: boolean;
  notificationEnabled: boolean;
  isSettingsOpen: boolean;
  language: string;
  setWorkDuration: (duration: number) => void;
  setShortBreakDuration: (duration: number) => void;
  setLongBreakDuration: (duration: number) => void;
  setLongBreakInterval: (interval: number) => void;
  setSoundEnabled: (enabled: boolean) => void;
  setNotificationEnabled: (enabled: boolean) => void;
  setLanguage: (language: string) => void;
  openSettings: () => void;
  closeSettings: () => void;
  saveSettings: (settings: {
    workDuration: number;
    shortBreakDuration: number;
    longBreakDuration: number;
    longBreakInterval: number;
    soundEnabled: boolean;
    notificationEnabled: boolean;
  }) => void;
  resetToDefaults: () => void;
}

const defaultSettings = {
  workDuration: 25,
  shortBreakDuration: 5,
  longBreakDuration: 15,
  longBreakInterval: 4,
  soundEnabled: true,
  notificationEnabled: false,
  isSettingsOpen: false,
  language: 'en',
};

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      ...defaultSettings,
      setWorkDuration: (duration) => set({ workDuration: duration }),
      setShortBreakDuration: (duration) => set({ shortBreakDuration: duration }),
      setLongBreakDuration: (duration) => set({ longBreakDuration: duration }),
      setLongBreakInterval: (interval) => set({ longBreakInterval: interval }),
      setSoundEnabled: (enabled) => set({ soundEnabled: enabled }),
      setNotificationEnabled: (enabled) => set({ notificationEnabled: enabled }),
      setLanguage: (language) => set({ language }),
      openSettings: () => set({ isSettingsOpen: true }),
      closeSettings: () => set({ isSettingsOpen: false }),
      saveSettings: (settings) => set({
        workDuration: settings.workDuration,
        shortBreakDuration: settings.shortBreakDuration,
        longBreakDuration: settings.longBreakDuration,
        longBreakInterval: settings.longBreakInterval,
        soundEnabled: settings.soundEnabled,
        notificationEnabled: settings.notificationEnabled,
        isSettingsOpen: false,
      }),
      resetToDefaults: () => set({
        workDuration: defaultSettings.workDuration,
        shortBreakDuration: defaultSettings.shortBreakDuration,
        longBreakDuration: defaultSettings.longBreakDuration,
        longBreakInterval: defaultSettings.longBreakInterval,
        soundEnabled: defaultSettings.soundEnabled,
        notificationEnabled: defaultSettings.notificationEnabled,
      }),
    }),
    {
      name: 'pomodo-settings',
      partialize: (state) => ({
        workDuration: state.workDuration,
        shortBreakDuration: state.shortBreakDuration,
        longBreakDuration: state.longBreakDuration,
        longBreakInterval: state.longBreakInterval,
        soundEnabled: state.soundEnabled,
        notificationEnabled: state.notificationEnabled,
        language: state.language,
      }),
    }
  )
);
